
import { library } from '@fortawesome/fontawesome-svg-core';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { PropType, defineComponent } from 'vue';

import parseDurationStringToSeconds from '@/shared/handsontable/rework/cellTypes/duration/parseDurationStringToSeconds';
import { parseSecondsToTime } from '@/shared/modules/parseTimeToSeconds';

import FormFieldInput from './FormFieldInput.vue';

library.add(faClock);

export default defineComponent({
  name: 'FormFieldInputTimePicker',
  components: { FormFieldInput },
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: String as PropType<string | null>,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    parser: {
      type: Function as PropType<(value: string) => string | null>,
      default: (s: string) => {
        const duration = parseDurationStringToSeconds(s);

        return duration !== null ? parseSecondsToTime(duration) : null;
      },
    },
  },
  data() {
    return {
      displayValue: this.value || '',
      hasPendingCommit: false,
    };
  },
  computed: {
    nativeTimeValue(): string {
      return /^\d{2}:\d{2}$/.test(this.displayValue) ? this.displayValue : '';
    },
  },
  watch: {
    value(newValue: string | null) {
      this.displayValue = newValue || '';
      this.hasPendingCommit = false;
    },
  },
  methods: {
    commitParsedValue(rawValue: string, emitChange = false): void {
      this.hasPendingCommit = false;
      const normalizedValue = rawValue.trim() === '' ? '00:00' : rawValue;
      const parsedValue = this.parser(normalizedValue);
      if (parsedValue === null) {
        this.$emit('invalid', rawValue);
        return;
      }

      this.displayValue = parsedValue;
      this.$emit('update', parsedValue);
      if (emitChange) {
        this.$emit('change', parsedValue);
      }
    },
    onInput(value: string): void {
      this.displayValue = value;
      this.hasPendingCommit = true;
      this.$emit('input', value);
    },
    onChange(value: string): void {
      this.displayValue = value;
      this.commitParsedValue(value, true);
    },
    onBlur(): void {
      if (this.hasPendingCommit) {
        this.commitParsedValue(this.displayValue);
      }
      this.$emit('blur');
    },
    onFocus(): void {
      this.$emit('focus');
    },
    onKeypress(event: KeyboardEvent): void {
      this.$emit('keypress', event);
    },
    onNativeTimeInput(event: Event): void {
      const value = event.target instanceof HTMLInputElement ? event.target.value : '';
      this.displayValue = value;
      this.$emit('input', value);
      this.commitParsedValue(value, true);
    },
    openNativeTimePicker(): void {
      const nativeTimeInput = this.$refs.nativeTimeInput as HTMLInputElement | undefined;
      if (!nativeTimeInput || this.disabled) {
        return;
      }

      const nativeTimeInputWithPicker = nativeTimeInput as HTMLInputElement & { showPicker?: () => void };
      if (typeof nativeTimeInputWithPicker.showPicker === 'function') {
        nativeTimeInputWithPicker.showPicker();
        return;
      }

      nativeTimeInput.focus();
      nativeTimeInput.click();
    },
  },
});
